import type { ImageValue } from '@/types/image';
import { normalizeImageValue } from '@/types/image';
import { normalizeVideoValue, type VideoValue } from '@/types/video';

export function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export function readTrimmedString(value: unknown): string | undefined {
  const raw = readString(value);
  if (raw === undefined) {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function readRecord(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return undefined;
}

/** Returns `raw` when it is a member of `allowed`; otherwise `fallback`. */
export function pickStringUnion<T extends string>(
  raw: string | undefined,
  allowed: readonly T[],
  fallback: T,
): T {
  if (raw === undefined) {
    return fallback;
  }
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

export function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

export function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

export function readPositiveNumber(value: unknown): number | undefined {
  const number = readNumber(value);
  return number !== undefined && Number.isFinite(number) && number > 0 ? number : undefined;
}

export const DEFAULT_FOCUS_POINT = 50;

export function buildFocusPointStyle(
  focusX: number,
  focusY: number,
): { objectPosition: string } | undefined {
  return focusX !== DEFAULT_FOCUS_POINT || focusY !== DEFAULT_FOCUS_POINT
    ? { objectPosition: `${focusX}% ${focusY}%` }
    : undefined;
}

export function resolveSectionFullWidth(props: Record<string, unknown>): boolean {
  return readBoolean(props.backgroundFullWidth) ?? false;
}

export function readAssetId(value: unknown): string | undefined {
  const record = readRecord(value);
  if (!record) return undefined;

  return readString(record.assetId) ?? readString(record.id);
}

export function readFolderId(value: unknown): string | undefined {
  const record = readRecord(value);
  if (!record) return undefined;

  return readString(record.folderId);
}

export function readImageUrl(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  const record = readRecord(value);
  if (!record) {
    return undefined;
  }

  const url = record.url;
  return typeof url === 'string' ? url : undefined;
}

export function readImageValue(value: unknown): ImageValue | undefined {
  return normalizeImageValue(value);
}

export function readVideoValue(value: unknown): VideoValue | undefined {
  return normalizeVideoValue(value);
}

export function cloneValue<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Previews rendered inside transformed/embedded scroll containers (e.g., scaled previews, iframes) break fixed/sticky background layers. */
export function sectionPropsForScaledPreview(
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (readBoolean(props.backgroundFixed) === false) return props;

  return { ...props, backgroundFixed: false };
}

export function withRemovalsAsNull(
  previousProps: Record<string, unknown> | undefined,
  nextProps: Record<string, unknown> | undefined,
): Record<string, unknown> {
  // Removed key => explicit null, so a restore through a merge API actually clears.
  const props = { ...(nextProps || {}) };

  for (const key of Object.keys(previousProps || {})) {
    if (!(key in props)) {
      props[key] = null;
    }
  }

  return props;
}
